import React, { useEffect, useState } from "react";
import axios from "axios";
import getApiBaseUrl from "../../services/getserver.service";
import styles from "../../styles/common/EnvironmentBanner.module.css";
import packageInfo from "../../../package.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faDatabase,
  faBoxesPacking,
  faTruck,
  faCreditCard,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";

interface EnvironmentInfo {
  systemEnvironment?: string;
  ratesEnvironment?: string;
  database?: {
    name: string;
    server: string;
    full: string;
  };
  ups?: {
    mode: string;
    apiUrl?: string;
  };
  liberty?: {
    mode: string;
    apiUrl?: string;
  };
  kraken?: {
    mode: string;
    apiUrl?: string;
  };
  stripe?: {
    mode: string;
    isLive: boolean;
  };
}

const isProductionEnvironment = (): boolean => {
  // 1. Variable de entorno de compilación (REACT_APP_IS_PROD=TRUE)
  const rawEnv = (process.env.REACT_APP_IS_PROD || "").trim().toUpperCase();
  if (rawEnv === "TRUE" || rawEnv === "PROD") {
    return true;
  }

  // 2. Validación de dominio / host en el navegador
  if (typeof window !== "undefined" && window.location) {
    const host = (window.location.hostname || "").toLowerCase();

    // Dominios oficiales de producción
    if (host === "elpaket.com" || host === "www.elpaket.com") {
      return true;
    }

    // Si NO es localhost ni IP privada ni subdominio de pruebas/test/dev
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.startsWith("192.168.") ||
      host.startsWith("10.") ||
      host.endsWith(".local");

    const isPruebas =
      host.includes("pruebas") ||
      host.includes("test") ||
      host.includes("dev") ||
      host.includes("staging");

    if (!isLocal && !isPruebas) {
      return true;
    }
  }

  return false;
};

const EnvironmentBanner: React.FC = () => {
  const isProduction = isProductionEnvironment();
  const [envData, setEnvData] = useState<EnvironmentInfo | null>(null);

  useEffect(() => {
    if (isProduction) return;

    let isMounted = true;
    const fetchEnvInfo = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await axios.get(`${baseUrl}/get-environment-info`, {
          timeout: 4000,
        });
        if (isMounted && response.data) {
          setEnvData(response.data);
        }
      } catch (error) {
        // En caso de que el backend no responda, mantenemos valores derivados del frontend
      }
    };

    fetchEnvInfo();
    return () => {
      isMounted = false;
    };
  }, [isProduction]);

  // Si estamos en ambiente de producción, NUNCA renderizar la franja
  if (isProduction) {
    return null;
  }

  // Valores calculados con fallback
  const rawEnv = (process.env.REACT_APP_IS_PROD || "").trim().toUpperCase();
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const ambienteLabel = isLocalhost
    ? "LOCAL / DEV"
    : rawEnv === "DEV"
    ? "DEV"
    : "PRUEBAS";

  const dbName = (envData?.database?.name || "").toLowerCase();
  const dbLabel = dbName.includes("pruebas") || dbName.includes("test")
    ? "PRUEBAS"
    : dbName.includes("dev")
    ? "DEV"
    : dbName.includes("enviar")
    ? "PROD"
    : rawEnv === "TRUE" || rawEnv === "PROD"
    ? "PROD"
    : rawEnv === "DEV"
    ? "DEV"
    : "PRUEBAS";

  const upsMode = envData?.ups?.mode || "Producción";
  const libertyMode = envData?.liberty?.mode || "Producción";
  const krakenMode = envData?.kraken?.mode || "Producción";

  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST || "";
  const isStripeTestKey = stripeKey.startsWith("pk_test");
  const stripeLabel =
    envData?.stripe?.mode ||
    (isStripeTestKey ? "SandBox / Test" : "Producción (Live)");

  const getModeBadgeClass = (modeStr: string) => {
    const lower = modeStr.toLowerCase();
    if (lower.includes("prod") || lower.includes("live")) {
      return `${styles.badge} ${styles.badgeProd}`;
    }
    if (lower.includes("sand")) {
      return `${styles.badge} ${styles.badgeSandbox}`;
    }
    return `${styles.badge} ${styles.badgeTest}`;
  };

  return (
    <div className={styles.bannerContainer}>
      <div className={styles.bannerContent}>
        {/* Ambiente */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faGlobe} /> Ambiente:
          </span>
          <span className={`${styles.badge} ${styles.badgeDev}`}>
            {ambienteLabel} ({packageInfo.version.startsWith("v") ? packageInfo.version : `v${packageInfo.version}`})
          </span>
        </div>

        {/* Base de Datos */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faDatabase} /> BD:
          </span>
          <span className={styles.itemValue}>{dbLabel}</span>
        </div>

        {/* Tarifa UPS */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faBoxesPacking} /> Tarifa UPS:
          </span>
          <span className={getModeBadgeClass(upsMode)}>{upsMode}</span>
        </div>

        {/* Tarifa Liberty */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faTruck} /> Tarifa Liberty:
          </span>
          <span className={getModeBadgeClass(libertyMode)}>{libertyMode}</span>
        </div>

        {/* Tarifa Kraken */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faServer} /> Tarifa Kraken:
          </span>
          <span className={getModeBadgeClass(krakenMode)}>{krakenMode}</span>
        </div>

        {/* Conexión Stripe */}
        <div className={styles.itemGroup}>
          <span className={styles.itemLabel}>
            <FontAwesomeIcon icon={faCreditCard} /> Conexión Stripe:
          </span>
          <span className={getModeBadgeClass(stripeLabel)}>
            {stripeLabel}
          </span>
        </div>

        {/* Live Indicator */}
        <span className={styles.liveDot} title="Conectado al entorno local / pruebas" />
      </div>
    </div>
  );
};

export default EnvironmentBanner;
